/**
 * Cronometro per fase del caricamento di un documento.
 *
 * PERCHE' ESISTE
 * Del percorso "scelgo un file -> vedo i materiali" si sapeva solo che a volte
 * era lento. Non si sapeva DOVE: se nella lettura in locale, nel caricamento
 * su Storage, nel server che estrae, nell'attesa dello studente prima di
 * premere Genera, o nell'elaborazione AI. Le misure del motore (45 s per 500k
 * caratteri, 149 s per 3M) valgono per una sola fase; senza le altre non si
 * puo' dire quale conviene accorciare. Questo modulo misura tutte le fasi per
 * ogni file e le manda in UN solo evento a fine giro, piu' uno per ogni
 * fallimento.
 *
 * COME SI USA
 * Le fasi partono dai punti di ingresso che gia' esistono, senza cambiarne
 * il comportamento: `apriGiro` nel router action, `registraAvanzamento` sugli
 * eventi di onProgress, `segnaClickGenera` sul pulsante, `iniziaFase` e
 * `chiudiFase` attorno alla chiamata AI, `chiudiGiro` quando l'esito e' noto.
 * Il file si riconosce per nome + dimensione, la stessa identita' della cache
 * documenti: e' l'unica che sopravvive al passaggio per Redux, dove del File
 * originale resta un oggetto piatto.
 *
 * Nessuna chiamata qui puo' lanciare: un cronometro che fa fallire un
 * caricamento riuscito e' peggio di nessun cronometro. L'orologio e'
 * sostituibile per i test.
 */
import { trackEvent } from '@utils/analytics';
import { motivoErroreEstrazione, MOTIVI } from '@utils/erroreEstrazione';

export const FASI = Object.freeze({
  ESTRAZIONE: 'estrazione',
  UPLOAD_STORAGE: 'upload_storage',
  SERVER_ESTRAZIONE: 'server_estrazione',
  ATTESA_UMANA: 'attesa_umana',
  SALVATAGGIO: 'salvataggio',
  ELABORAZIONE_AI: 'elaborazione_ai',
});

export const STRADE = Object.freeze({
  LOCALE: 'locale',
  MULTIPART: 'multipart',
  STORAGE: 'storage',
  SOTTOFONDO: 'sottofondo',
  // Testo gia' estratto in un giro precedente (utils/documentCache): nessuna
  // lettura, ma e' una strada vera e va distinta dalle altre.
  CACHE: 'cache',
});

export const EVENTO_GIRO = 'document_pipeline';
export const EVENTO_FALLIMENTO = 'document_pipeline_failed';

// Formati che, sotto la soglia del worker e senza eventi di avanzamento,
// si leggono nel browser: pdf.js, mammoth, testo puro.
const TIPI_LETTI_IN_LOCALE = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/markdown',
];

let ora = () => Date.now();
const giri = new Map();

/** Solo per i test: un orologio che si controlla a mano. */
export const __impostaOrologioPerTest = (fn) => {
  ora = typeof fn === 'function' ? fn : () => Date.now();
};

/** Solo per i test: azzera i giri aperti. */
export const __azzeraPerTest = () => giri.clear();

const dimensione = (file) => {
  const n = file?.size ?? file?.fileSize;
  return Number.isFinite(n) ? n : null;
};

/** Identita' del file lungo tutto il percorso: nome + byte. */
export const chiaveFile = (file) => {
  if (!file) return null;
  const nome = file.name || '';
  if (!nome) return null;
  const byte = dimensione(file);
  return byte === null ? nome : `${nome}|${byte}`;
};

const trova = (chiave) => (chiave ? giri.get(chiave) : undefined) || null;

/**
 * Apre il giro di un file. Se ne esisteva uno con la stessa identita' (lo
 * stesso file ricaricato), lo sostituisce: il vecchio non aveva un esito e
 * non lo avra' mai.
 */
export const apriGiro = (file, { strada = null } = {}) => {
  try {
    const chiave = chiaveFile(file);
    if (!chiave) return null;
    giri.set(chiave, {
      chiave,
      nome: file.name,
      tipo: file.type || '',
      byte: dimensione(file),
      strada,
      stradaFonte: strada ? 'dichiarata' : null,
      caratteri: null,
      percorso: null,
      inizio: ora(),
      fasi: {},
      eventi: [],
    });
    return chiave;
  } catch (_) {
    return null;
  }
};

export const iniziaFase = (chiave, fase) => {
  const giro = trova(chiave);
  if (!giro || !fase) return;
  if (giro.fasi[fase] && giro.fasi[fase].inizio != null) return;
  giro.fasi[fase] = { inizio: ora(), fine: null };
};

export const chiudiFase = (chiave, fase) => {
  const giro = trova(chiave);
  if (!giro || !giro.fasi[fase] || giro.fasi[fase].fine != null) return;
  giro.fasi[fase].fine = ora();
};

export const segnaStrada = (chiave, strada, fonte = 'dichiarata') => {
  const giro = trova(chiave);
  if (!giro || !strada) return;
  giro.strada = strada;
  giro.stradaFonte = fonte;
};

export const segnaCaratteri = (chiave, caratteri) => {
  const giro = trova(chiave);
  if (!giro) return;
  giro.caratteri = Number.isFinite(caratteri) ? caratteri : null;
};

/** 'sync' (200 tenuto aperto) o 'queue' (202 e listener). */
export const segnaPercorso = (chiave, percorso) => {
  const giro = trova(chiave);
  if (!giro) return;
  giro.percorso = percorso || null;
};

/**
 * Legge gli eventi di onProgress e ne ricava strada e fasi intermedie.
 *
 * Le due sorgenti si distinguono dalla chiave dell'evento, ed e' un fatto del
 * codice che esiste, non una convenzione inventata qui: il condotto largo
 * (services/condottoLargo.js) manda `{ fase }` con 'upload' = caricamento su
 * Storage e 'extracting' = il server legge da GCS; il worker
 * (workers/fileExtractWorker.js) manda `{ phase }` con 'extracting' = pdf.js
 * in locale, 'upload' = invio multipart al server, 'processing' = il server
 * elabora.
 */
export const registraAvanzamento = (chiave, evento) => {
  const giro = trova(chiave);
  if (!giro || !evento || typeof evento !== 'object') return;
  const dalCondotto = Object.prototype.hasOwnProperty.call(evento, 'fase');
  const nome = dalCondotto ? evento.fase : evento.phase;
  if (!nome) return;
  giro.eventi.push(nome);
  // Una strada dichiarata all'apertura (sottofondo, cache) resta: gli eventi
  // dicono da dove passa il file, non chi ha deciso di mandarcelo.
  const dagliEventi = (strada) => {
    if (giro.stradaFonte !== 'dichiarata') segnaStrada(chiave, strada, 'eventi');
  };

  if (dalCondotto) {
    if (nome === 'upload') {
      dagliEventi(STRADE.STORAGE);
      iniziaFase(chiave, FASI.UPLOAD_STORAGE);
    } else if (nome === 'extracting') {
      chiudiFase(chiave, FASI.UPLOAD_STORAGE);
      iniziaFase(chiave, FASI.SERVER_ESTRAZIONE);
    }
    return;
  }

  if (nome === 'upload') {
    dagliEventi(STRADE.MULTIPART);
  } else if (nome === 'processing') {
    dagliEventi(STRADE.MULTIPART);
    iniziaFase(chiave, FASI.SERVER_ESTRAZIONE);
  } else if (nome === 'extracting' && !giro.strada) {
    dagliEventi(STRADE.LOCALE);
  }
};

/**
 * Il testo e' pronto: chiude l'estrazione (e le sue fasi interne ancora
 * aperte), deduce la strada se nessun evento l'ha detta, e apre l'attesa
 * dello studente.
 */
export const testoPronto = (chiave, caratteri) => {
  const giro = trova(chiave);
  if (!giro) return;
  chiudiFase(chiave, FASI.UPLOAD_STORAGE);
  chiudiFase(chiave, FASI.SERVER_ESTRAZIONE);
  chiudiFase(chiave, FASI.ESTRAZIONE);
  segnaCaratteri(chiave, caratteri);
  if (!giro.strada) {
    const locale = TIPI_LETTI_IN_LOCALE.includes(giro.tipo) || /\.(md|markdown|txt)$/i.test(giro.nome);
    segnaStrada(chiave, locale ? STRADE.LOCALE : STRADE.MULTIPART, 'tipo');
  }
  iniziaFase(chiave, FASI.ATTESA_UMANA);
};

/** Lo studente ha premuto Genera: finisce l'attesa, comincia il salvataggio. */
export const segnaClickGenera = (files) => {
  try {
    (files || []).forEach((file) => {
      const chiave = chiaveFile(file);
      chiudiFase(chiave, FASI.ATTESA_UMANA);
      iniziaFase(chiave, FASI.SALVATAGGIO);
    });
  } catch (_) {
    // mai far fallire il click
  }
};

const durata = (fase, adesso) => {
  if (!fase || fase.inizio == null) return null;
  const fine = fase.fine != null ? fase.fine : adesso;
  return Math.max(0, fine - fase.inizio);
};

/** Funzione pura: le durate di un giro, in millisecondi. */
export const calcolaDurate = (giro, adesso = ora()) => ({
  estrazione: durata(giro.fasi[FASI.ESTRAZIONE], adesso),
  upload_storage: durata(giro.fasi[FASI.UPLOAD_STORAGE], adesso),
  server_estrazione: durata(giro.fasi[FASI.SERVER_ESTRAZIONE], adesso),
  attesa_umana: durata(giro.fasi[FASI.ATTESA_UMANA], adesso),
  salvataggio: durata(giro.fasi[FASI.SALVATAGGIO], adesso),
  elaborazione_ai: durata(giro.fasi[FASI.ELABORAZIONE_AI], adesso),
  totale: Math.max(0, adesso - giro.inizio),
});

const tronca = (s, n) => (typeof s === 'string' ? s.slice(0, n) : s);

/**
 * Funzione pura: il payload dell'evento. Le durate assenti non ci sono
 * proprio (GA4 non vuole null), i nomi sono in inglese come gli altri eventi
 * del repo (file_size_mb, error_code).
 */
export const costruisciPayload = (giro, { esito, errore = null, faseFallita = null } = {}, adesso = ora()) => {
  const d = calcolaDurate(giro, adesso);
  const payload = {
    category: 'upload',
    file_name: tronca(giro.nome, 100),
    file_type: tronca(giro.tipo || 'unknown', 100),
    file_size_bytes: giro.byte ?? 0,
    route: giro.strada || 'unknown',
    route_source: giro.stradaFonte || 'unknown',
    pipeline: giro.percorso || 'unknown',
    chars_extracted: giro.caratteri ?? 0,
    outcome: esito,
    total_ms: d.totale,
  };
  const mappa = {
    extraction_ms: d.estrazione,
    storage_upload_ms: d.upload_storage,
    server_extraction_ms: d.server_estrazione,
    human_wait_ms: d.attesa_umana,
    save_ms: d.salvataggio,
    ai_processing_ms: d.elaborazione_ai,
  };
  Object.entries(mappa).forEach(([k, v]) => {
    if (v != null) payload[k] = v;
  });
  if (faseFallita) payload.failed_phase = faseFallita;
  if (errore) payload.error_message = tronca(String(errore), 100);
  return payload;
};

const manda = (nome, payload) => {
  try {
    trackEvent(nome, payload);
  } catch (_) {
    // l'analytics non deve mai rompere il caricamento
  }
};

/** Fine giro con esito noto: UN evento, poi il giro sparisce. */
export const chiudiGiro = (chiave, { esito = 'ok', errore = null, faseFallita = null } = {}) => {
  const giro = trova(chiave);
  if (!giro) return null;
  Object.values(FASI).forEach((fase) => chiudiFase(chiave, fase));
  const payload = costruisciPayload(giro, { esito, errore, faseFallita });
  manda(EVENTO_GIRO, payload);
  giri.delete(chiave);
  return payload;
};

/**
 * Il `reason` del fallimento: uno dei codici di utils/erroreEstrazione, gli
 * stessi dell'avviso e della card (STU-424). Chi lo sa lo passa (il router
 * action, che ha anche il codice HTTP); per gli altri si ricava dal
 * messaggio e dal tipo del file.
 */
const motivoDelFallimento = (giro, motivo, errore) => {
  if (motivo) return motivo;
  try {
    return motivoErroreEstrazione({ messaggio: errore ? String(errore) : '', tipo: giro.tipo });
  } catch (_) {
    return MOTIVI.GENERICO;
  }
};

/** Un fallimento: l'evento del fallimento, poi il giro si chiude in errore. */
export const segnaFallimento = (chiave, { fase = null, errore = null, motivo = null } = {}) => {
  const giro = trova(chiave);
  if (!giro) return null;
  const payload = costruisciPayload(giro, { esito: 'errore', errore, faseFallita: fase });
  payload.reason = motivoDelFallimento(giro, motivo, errore);
  manda(EVENTO_FALLIMENTO, payload);
  return chiudiGiro(chiave, { esito: 'errore', errore, faseFallita: fase });
};

/** Chiude tutti i giri dei file passati con lo stesso esito. */
export const chiudiGiri = (files, esito) => {
  try {
    (files || []).forEach((file) => chiudiGiro(chiaveFile(file), esito));
  } catch (_) {
    // vedi sopra
  }
};

export const segnaFallimenti = (files, dettaglio) => {
  try {
    (files || []).forEach((file) => segnaFallimento(chiaveFile(file), dettaglio));
  } catch (_) {
    // vedi sopra
  }
};

export const __giroPerTest = (chiave) => trova(chiave);
